import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, Plus, Minus, X, ChefHat } from "lucide-react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function OnlineOrderPage() {
  const getVisibleCount = () => {
    if (window.innerWidth < 640) return 2; // mobile
    if (window.innerWidth < 768) return 3; // small tablet
    if (window.innerWidth < 1280) return 6; // tablet
    return 10; // desktop
  };

  const [visibleCount, setVisibleCount] = useState(getVisibleCount());
  const [startIndex, setStartIndex] = useState(0);
  const { restaurantSlug } = useParams();
  const navigate = useNavigate();

  const [restaurant, setRestaurant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [creditSlabs, setCreditSlabs] = useState([]);

  // Customer info
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [showCheckout, setShowCheckout] = useState(false);
  const categoryScrollRef = useRef(null);
  const [isMainCartLocked, setIsMainCartLocked] = useState(false);
  const [lockedCredit, setLockedCredit] = useState(0);
  const [extraCart, setExtraCart] = useState([]);
  const [remainingCredit, setRemainingCredit] = useState(0);
  const [showExtraItems, setShowExtraItems] = useState(false);

  useEffect(() => {
    fetchRestaurant();
  }, [restaurantSlug]);

  useEffect(() => {
    if (restaurant) {
      fetchCategories();
      fetchMenuItems();
    }
  }, [restaurant]);

  useEffect(() => {
    if (restaurant) {
      fetchCreditSlabs();
    }
  }, [restaurant]);

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleCount());
      setStartIndex(0); // reset so UI never breaks
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchCreditSlabs = async () => {
    try {
      const response = await axios.get(
        `${API}/public/credit-slabs?restaurant_id=${restaurant.id}`
      );

      setCreditSlabs(response.data);
    } catch (error) {
      console.error("Error fetching credit slabs:", error);
    }
  };
  const lockMainCart = () => {
    const credit = calculateCredit();
    setLockedCredit(credit);
    setRemainingCredit(credit);
    setIsMainCartLocked(true);
  };

  const fetchRestaurant = async () => {
    try {
      const response = await axios.get(
        `${API}/public/restaurant/${restaurantSlug}`
      );
      setRestaurant(response.data);
    } catch (error) {
      console.error("Error fetching restaurant:", error);
      toast.error("Restaurant not found");
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${API}/public/categories?restaurant_id=${restaurant.id}`
      );
      setCategories(response.data);
      if (response.data.length > 0) {
        setSelectedCategory("all");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(
        `${API}/public/menu/items?restaurant_id=${restaurant.id}`
      );
      setMenuItems(response.data);
    } catch (error) {
      console.error("Error fetching menu items:", error);
    }
  };

  const addToCart = (item) => {
    const existing = cart.find((c) => c.id === item.id);
    if (existing) {
      setCart(
        cart.map((c) =>
          c.id === item.id ? { ...c, quantity: c.quantity + 1 } : c
        )
      );
    } else {
      setCart([...cart, { ...item, quantity: 1 }]);
    }
    toast.success(`${item.name} added to cart`);
  };

  const updateQuantity = (itemId, change) => {
    if (isMainCartLocked) {
      toast.error("Main order is locked. You can only add extra items now.");
      return;
    }

    setCart(
      cart
        .map((item) => {
          if (item.id === itemId) {
            const newQty = item.quantity + change;
            return newQty > 0 ? { ...item, quantity: newQty } : null;
          }
          return item;
        })
        .filter(Boolean)
    );
  };

  const removeFromCart = (itemId) => {
    if (isMainCartLocked) {
      toast.error("Main order is locked. You can only add extra items now.");
      return;
    }
    setCart(cart.filter((item) => item.id !== itemId));
  };

  const addExtraItem = (item) => {
    const price = item.pricing?.takeaway || item.price || 0;

    if (remainingCredit >= price) {
      // Full credit covers the item
      setRemainingCredit(remainingCredit - price);
      setExtraCart([...extraCart, { ...item, quantity: 1, payable: 0 }]);
    } else {
      // Partial credit
      const payable = price - remainingCredit;
      setRemainingCredit(0);
      setExtraCart([...extraCart, { ...item, quantity: 1, payable }]);
    }

    toast.success(`${item.name} added as extra item`);
  };

  const calculateCredit = () => {
    const total = calculateTotal();

    if (!creditSlabs || creditSlabs.length === 0) return 0;

    // Find matching slab
    const slab = creditSlabs.find(
      (s) => total >= s.min_value && total <= s.max_value
    );

    if (!slab) return 0;

    const credit = (total * slab.credit_percent) / 100;
    return credit;
  };

  const calculateTotal = () => {
    return cart.reduce(
      (sum, item) =>
        sum + (item.pricing?.takeaway || item.price || 0) * item.quantity,
      0
    );
  };
  const extraItemsTotal = () => {
    return extraCart.reduce((sum, item) => {
      const price = item.pricing?.takeaway || item.price || 0;
      return sum + price;
    }, 0);
  };

  const extraItemsPayable = () => {
    return extraCart.reduce((sum, item) => sum + item.payable, 0);
  };

  const totalCreditUsed = () => {
    return lockedCredit - remainingCredit;
  };

  const finalGrandTotal = () => {
    // main bill + payable for extra items
    return calculateTotal() + extraItemsPayable();
  };

  const handlePlaceOrder = async () => {
    if (!customerName || !customerPhone) {
      toast.error("Please enter your name and phone number");
      return;
    }

    if (cart.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    const mergedItems = [
      ...cart.map((item) => ({
        item_id: item.id,
        item_name: item.name,
        quantity: item.quantity,
        price: item.pricing?.takeaway || item.price || 0,
        tax: item.tax || 0,
        is_extra_item: false,
      })),

      ...extraCart.map((item) => ({
        item_id: item.id,
        item_name: item.name,
        quantity: 1,
        price: item.payable,
        tax: item.tax || 0,
        is_extra_item: true,
        credit_used: (item.pricing?.takeaway || item.price || 0) - item.payable,
      })),
    ];

    try {
      const orderPayload = {
        order_type: "online",
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_address: deliveryAddress,
        items: mergedItems,
        order_status: "pending",
        payment_status: "pending",
      };

      await axios.post(
        `${API}/orders/customer?restaurant_id=${restaurant.id}`,
        orderPayload
      );

      toast.success("Order placed successfully!");
      setCart([]);
      setShowCheckout(false);
      setCustomerName("");
      setCustomerPhone("");
      setDeliveryAddress("");

      // Show success message
      alert(
        "Thank you! Your order has been received. The restaurant will contact you shortly."
      );
    } catch (error) {
      console.error("Error placing order:", error);
      toast.error("Failed to place order. Please try again.");
    }
  };

  const filteredItems = menuItems;

  const groupedItems = categories.reduce((acc, cat) => {
    const categoryItems = filteredItems.filter(
      (item) => item.category_id === cat.id
    );

    const subGrouped = categoryItems.reduce((subAcc, item) => {
      const key = item.sub_category_id || "no-sub";
      if (!subAcc[key]) subAcc[key] = [];
      subAcc[key].push(item);
      return subAcc;
    }, {});

    acc[cat.id] = subGrouped;
    return acc;
  }, {});

  const filteredCategories = categories.filter((cat) => {
    const subGroups = groupedItems[cat.id];
    return subGroups
      ? Object.values(subGroups).some((items) => items.length > 0)
      : false;
  });

  useEffect(() => {
    setStartIndex(0);
  }, [filteredCategories.length, visibleCount]);

  const canGoLeft = startIndex > 0;
  const canGoRight = startIndex + visibleCount < filteredCategories.length;

  const visibleCategories = filteredCategories.slice(
    startIndex,
    startIndex + visibleCount
  );

  if (!restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-700 mb-2">Loading...</h1>
          <p className="text-gray-500">Please wait while we load the menu</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}

      <div className="bg-gradient-to-r from-red-600 to-red-700 shadow-lg sticky top-0 z-50">
        <div className="p-6 text-white max-w-6xl mx-auto">
          <div className="flex items-center gap-4">
            <ChefHat className="w-10 h-10 text-white flex-shrink-0" />
            <div>
              <h1 className="text-3xl font-bold leading-tight">
                {restaurant.name}
              </h1>
              {restaurant.address && (
                <p className="text-gray-200 text-sm mt-1">
                  {restaurant.address}
                </p>
              )}
              <p className="text-red-100 text-sm">Online Ordering</p>
            </div>
          </div>
        </div>
        <div className="sticky top-[96px] z-40 bg-white">
          <div className="relative flex items-center px-[7rem] gap-2 border-b py-2 overflow-hidden">
            <button
              onClick={() => {
                setSelectedCategory("all");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap flex-shrink-0
      border-b-4 transition-colors
      ${
        selectedCategory === "all"
          ? "border-red-600 text-red-600"
          : "border-transparent text-gray-500 hover:text-red-600"
      }`}
            >
              All
            </button>
            {visibleCategories?.length >= visibleCount && (
              <button
                disabled={!canGoLeft}
                onClick={() =>
                  setStartIndex((p) => Math.max(p - visibleCount, 0))
                }
                className={`p-1 rounded-full w-max h-max
      ${
        canGoLeft
          ? "text-white bg-red-600"
          : "text-white bg-gray-200 cursor-not-allowed"
      }`}
              >
                <ChevronLeft size={18} />
              </button>
            )}
            <div
              ref={categoryScrollRef}
              className="flex gap-4 overflow-hidden flex-nowrap"
            >
              {visibleCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setSelectedCategory(cat.id);
                    const el = document.getElementById(`category-${cat.id}`);
                    if (el) {
                      const y =
                        el.getBoundingClientRect().top + window.scrollY - 190;
                      window.scrollTo({ top: y, behavior: "smooth" });
                    }
                  }}
                  className={`px-2 py-2 text-sm font-medium whitespace-nowrap flex-shrink-0
          border-b-4 transition-colors
          ${
            selectedCategory === cat.id
              ? "border-red-600 text-red-600"
              : "border-transparent text-gray-500 hover:text-red-600"
          }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
            {visibleCategories?.length >= visibleCount && (
              <button
                disabled={!canGoRight}
                onClick={() =>
                  setStartIndex((prev) =>
                    Math.min(
                      prev + visibleCount,
                      Math.max(filteredCategories.length - visibleCount, 0)
                    )
                  )
                }
                className={`p-1 rounded-full w-max h-max
      ${
        canGoRight
          ? "text-white bg-red-600"
          : "text-white bg-gray-200 cursor-not-allowed"
      }`}
              >
                <ChevronRight size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
      {/* Cart Button */}
      <button
        onClick={() => setShowCart(!showCart)}
        className="fixed bottom-6 right-6 bg-red-600 text-white rounded-full p-4 shadow-2xl z-50 hover:bg-red-700 transition-all"
      >
        <ShoppingCart size={24} />
        {cart.length > 0 && (
          <span className="absolute -top-2 -right-2 bg-yellow-400 text-red-900 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
            {cart.length}
          </span>
        )}
      </button>

      <div className="max-w-6xl mx-auto p-4  bg-[#f9f5f5]">
        <div>
          {Object.entries(groupedItems).map(([categoryId, subGroups]) => {
            const hasItems = Object.values(subGroups).some(
              (items) => items.length > 0
            );

            if (!hasItems) return null;

            return (
              <div key={categoryId}>
                {/* CATEGORY TITLE */}
                <h3
                  id={`category-${categoryId}`}
                  className="font-bold text-3xl text-gray-800 mb-6 scroll-mt-[190px]"
                >
                  {categories.find((cat) => cat.id === categoryId)?.name}
                </h3>

                {/* SUB CATEGORIES */}
                {Object.entries(subGroups).map(([subId, items]) => {
                  if (items.length === 0) return null;

                  return (
                    <div key={subId} className="mb-6">
                      {subId !== "no-sub" && (
                        <h4 className="text-xl font-semibold text-gray-700 mb-4 pl-1">
                          Sub Category
                        </h4>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {items.map((item) => (
                          <Card
                            key={item.id}
                            className="hover:shadow-lg transition-shadow border border-gray-200"
                          >
                            <CardContent className="p-4">
                              <h3 className="font-bold text-lg text-gray-800 mb-1">
                                {item.name}
                              </h3>

                              {item.description && (
                                <p className="text-sm text-gray-500 mb-2 line-clamp-2">
                                  {item.description}
                                </p>
                              )}

                              <p className="text-xl font-bold text-red-600 mb-3">
                                $
                                {(
                                  item.pricing?.takeaway ||
                                  item.price ||
                                  0
                                ).toFixed(2)}
                              </p>

                              <Button
                                onClick={() => addToCart(item)}
                                className="w-full bg-red-600 hover:bg-red-700"
                              >
                                <Plus size={16} className="mr-2" />
                                Add
                              </Button>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  );
                })}

                <div className="border-t border-gray-100 mt-6"></div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
          <div
            className="fixed right-0 top-0 h-full w-full md:w-96 bg-white shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Order Summary</h2>
                <button
                  onClick={() => setShowCart(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              {cart.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No items added yet. Start adding delicious items to your
                  order.
                </p>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    {cart.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center gap-3 border-b pb-3"
                      >
                        <div className="flex-1">
                          <h4 className="font-semibold">{item.name}</h4>
                          <p className="text-sm text-gray-600">
                            $
                            {(
                              item.pricing?.takeaway ||
                              item.price ||
                              0
                            ).toFixed(2)}{" "}
                            each
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(item.id, -1)}
                            className="bg-gray-200 rounded-full p-1 hover:bg-gray-300"
                          >
                            <Minus size={16} />
                          </button>
                          <span className="w-8 text-center font-semibold">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, 1)}
                            className="bg-gray-200 rounded-full p-1 hover:bg-gray-300"
                          >
                            <Plus size={16} />
                          </button>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="text-red-600 hover:text-red-700 ml-2"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t pt-4 mb-6 space-y-2">
                    {/* Subtotal */}
                    <div className="flex justify-between text-lg">
                      <span>Subtotal:</span>
                      <span className="font-semibold">
                        ${calculateTotal().toFixed(2)}
                      </span>
                    </div>

                    {/* <div className="flex justify-between text-xl font-bold pt-2 border-t">
                      <span>Grand Total:</span>
                      <span className="text-red-600">${calculateTotal().toFixed(2)}</span>
                    </div> */}
                  </div>

                  <button
                    onClick={() => {
                      lockMainCart();
                      setShowExtraItems(true);
                      setShowCart(false);
                    }}
                    className="w-full bg-red-600 text-white py-3 rounded-lg font-bold hover:bg-red-700 transition-all"
                  >
                    Proceed to Checkout & Earn Credit
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {showExtraItems && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
          <div
            className="fixed right-0 top-0 h-full w-full md:w-96 bg-white shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Redeem Your Credit</h2>
                <button
                  onClick={() => setShowExtraItems(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>
              <p className="text-green-600 text-sm font-medium mb-4">
                🎁 Apply your credit to add extras — see your total drop
                instantly!
              </p>

              <p className="text-green-700 font-semibold mb-2">
                Remaining Credit: ${remainingCredit.toFixed(2)}
              </p>
              <div className="max-h-64 overflow-y-auto border rounded p-2 bg-gray-50 mt-4">
                {menuItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex justify-between items-center py-2 border-b"
                  >
                    <div>
                      <h4 className="font-semibold">{item.name}</h4>
                      <p className="text-gray-500">
                        ${(item.pricing?.takeaway || item.price).toFixed(2)}
                      </p>
                    </div>
                    <button
                      className="bg-red-600 text-white px-3 py-1 rounded"
                      onClick={() => addExtraItem(item)}
                    >
                      Add
                    </button>
                  </div>
                ))}
              </div>

              {/* Extra Items Summary */}
              <h3 className="text-xl font-bold mt-4">Extra Items Summary</h3>
              {extraCart.length === 0 ? (
                <p className="text-gray-500">No extra items added.</p>
              ) : (
                <div className="space-y-2">
                  {extraCart.map((item) => (
                    <div key={item.id} className="flex justify-between py-1">
                      <span>{item.name}</span>
                      <span
                        className={
                          item.payable === 0 ? "text-green-600" : "text-red-600"
                        }
                      >
                        {item.payable === 0
                          ? "FREE (Credit Applied)"
                          : `$${item.payable}`}
                      </span>
                    </div>
                  ))}

                  {/* Full totals */}
                  <div className="border-t pt-3 mt-4 space-y-2 text-lg">
                    <div className="flex justify-between">
                      <span>Original Bill Amount:</span>
                      <span className="font-semibold">
                        ${calculateTotal().toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-green-700">
                      <span>Credit Earned from Order:</span>
                      <span className="font-semibold">
                        ${lockedCredit.toFixed(2)}
                      </span>
                    </div>
                    {/* <div className="flex justify-between text-blue-700">
                      <span>Credit Redeemed:</span>
                      <span className="font-semibold">${totalCreditUsed().toFixed(2)}</span>
                    </div> */}
                    <div className="flex justify-between">
                      <span>Extra Items Value:</span>
                      <span>${extraItemsTotal().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-red-600 font-bold">
                      <span>Payable for Extra Items:</span>
                      <span>${extraItemsPayable().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xl font-bold border-t pt-3">
                      <span>Total Amount to Pay:</span>
                      <span className="text-red-700">
                        ${finalGrandTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <button
                className="w-full mt-6 bg-green-600 text-white py-3 rounded font-bold"
                onClick={() => {
                  setShowExtraItems(false);
                  setShowCheckout(true);
                }}
              >
                Continue to Checkout
              </button>

              <button
                className="w-full mt-2 bg-gray-300 py-3 rounded"
                onClick={() => setShowExtraItems(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold mb-4">Contact Information</h2>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Your name"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="Your phone number"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Delivery Address (Optional)
                </label>
                <textarea
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  placeholder="Your delivery address"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCheckout(false)}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handlePlaceOrder}
                className="flex-1 bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700"
              >
                Place Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
